import { memo, useCallback, useEffect, useRef, useState } from "react";
import { PageHead } from "@/components/PageHead";
import { useCursorTail } from "@/lib/hooks/useCursorTail";
import { hhmmss, POLL } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fota, type FotaEvent } from "@/api/client";

import { ActionRow } from "@/components/ActionRow";
import { Icon } from "@/components/icons";
const MAX_TERMINALS = 5;

const KIND_COLOR: Record<FotaEvent["kind"], string> = {
  health: "var(--t-mute)",
  command: "var(--info)",
  decision: "var(--warn)",
  // A COMMAND REFUSAL. Louder than a decision, quieter than an error: the
  // device said no to the COMMAND and carried on doing what it was doing.
  warn: "var(--warn)",
  error: "var(--bad)",
};
const KIND_LABEL: Record<FotaEvent["kind"], string> = {
  health: "HEALTH",
  command: "CMD →",
  decision: "DECIDE",
  warn: "REFUSED",
  error: "ERROR",
};


/** Live JS-heap badge (Chrome only) — evidence for the "Debug page eats RAM"
 * question: shows current heap + drift since the page opened. A real leak in
 * OUR code shows as `used` climbing without plateau; if this stays flat while
 * Chrome's overall number grows, the growth is outside the JS heap. */
function HeapBadge() {
  const [txt, setTxt] = useState("");
  const base = useRef<number | null>(null);
  useEffect(() => {
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    if (!mem) return;  // non-Chromium
    const fmt = (b: number) => `${(b / 1048576).toFixed(0)} MB`;
    const tick = () => {
      const used = mem.usedJSHeapSize;
      if (base.current === null) base.current = used;
      const d = used - base.current;
      setTxt(`heap ${fmt(used)} (${d >= 0 ? "+" : "−"}${fmt(Math.abs(d))} since open)`);
    };
    tick();
    const t = setInterval(tick, 10000);
    return () => clearInterval(t);
  }, []);
  if (!txt) return null;
  return <span className="mono" style={{ fontSize: 11, color: "var(--t-dim)" }} title="JS heap of this tab — climbs without plateau = leak in Orbit code; flat while Chrome grows = elsewhere">{txt}</span>;
}

/** One live terminal per armed IMEI (up to MAX_TERMINALS). Arm an IMEI → its
 *  terminal appears; disarm → it's removed. Each terminal is independent. */
export function FotaDebugPage() {
  const qc = useQueryClient();
  const [imei, setImei] = useState("");

  const watched = useQuery({
    queryKey: ["fota", "watched"],
    queryFn: fota.watched,
    refetchInterval: POLL.STATE,
  });
  const rows = watched.data?.watched ?? [];
  const shown = rows.slice(0, MAX_TERMINALS);
  const full = rows.length >= MAX_TERMINALS;

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["fota", "watched"] });
  const arm = useMutation({ mutationFn: (v: string) => fota.arm(v), onSuccess: () => { setImei(""); invalidate(); } });
  const disarm = useMutation({ mutationFn: (v: string) => fota.disarm(v), onSuccess: invalidate });

  const canArm = imei.trim().length > 0 && !full && !rows.some((w) => w.imei === imei.trim());

  return (
    <div className="mpage on">
      <PageHead eyebrow="FOTA · Debug" title="Device debug"
        desc={<>Arm an IMEI to open a live terminal for it — decoded heartbeats, the commands Orbit sends (update / apply / reset), rollout decisions, and failures with the cause. Each armed IMEI gets its own terminal (up to {MAX_TERMINALS}); only armed IMEIs are logged.</>} />

      <div className="panel panel-pad" style={{ marginBottom: 12 }}>
        <ActionRow>
          <input
            className="search mono"
            style={{ minWidth: 240 }}
            placeholder="IMEI, e.g. 865510080777172"
            value={imei}
            onChange={(e) => setImei(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && canArm && arm.mutate(imei.trim())}
          />
          <button className="btn teal" disabled={!canArm || arm.isPending} onClick={() => arm.mutate(imei.trim())}>
            ● Open terminal
          </button>
          <span style={{ fontSize: 12, color: "var(--t-mute)" }}>
            {rows.length}/{MAX_TERMINALS} terminal{rows.length === 1 ? "" : "s"}
            {full && <span style={{ color: "var(--warn)" }}> · max reached — disarm one to open another</span>}
          </span>
          <span style={{ marginLeft: "auto" }}><HeapBadge /></span>
        </ActionRow>
      </div>

      {shown.length === 0 ? (
        <div className="panel panel-pad cap">
          No terminals open. Arm an IMEI above, then run a FOTA update against it.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {shown.map((w) => (
            <EventConsole key={w.imei} imei={w.imei} onDisarm={() => disarm.mutate(w.imei)} />
          ))}
        </div>
      )}
    </div>
  );
}

// Paused terminals, per IMEI — module-scoped so a pause SURVIVES navigating away
// and back (component state would silently reset to running on remount).
const PAUSED_TERMINALS = new Set<string>();

/** One event line, memoized: the terminal holds up to 500 of these and appends
 *  every few seconds during an active FOTA — without memo every append
 *  re-rendered all 500. Only the toggled row re-renders on expand/collapse. */
const EventLine = memo(function EventLine({ e, imei, expanded, onToggle }: {
  e: FotaEvent; imei: string; expanded: boolean; onToggle: (id: number) => void;
}) {
  return (
    <div>
      <div
        onClick={() => onToggle(e.id)}
        style={{ cursor: e.detail ? "pointer" : "default", display: "flex", gap: 7, whiteSpace: "pre-wrap" }}
      >
        <span style={{ color: "var(--t-dim)", fontSize: 11 }}>{imei}</span>
        <span className="muted">{hhmmss(e.ts)}</span>
        <span style={{ color: KIND_COLOR[e.kind], fontWeight: 600, minWidth: 52 }}>{KIND_LABEL[e.kind]}</span>
        <span style={{ color: e.kind === "error" ? "var(--bad)" : e.kind === "warn" ? "var(--warn)" : "var(--t-bright)" }}>{e.summary}</span>
      </div>
      {expanded && e.detail && (
        <pre style={{ margin: "2px 0 6px 60px", padding: 8, background: "rgba(0,0,0,.22)", borderRadius: 6, color: "var(--t-mute)", overflowX: "auto" }}>
          {JSON.stringify(e.detail, null, 2)}
        </pre>
      )}
    </div>
  );
});

function EventConsole({ imei, onDisarm }: { imei: string; onDisarm: () => void }) {
  const [open, setOpen] = useState<number | null>(null);
  const [paused, setPausedState] = useState(() => PAUSED_TERMINALS.has(imei));
  const setPaused = (fn: (p: boolean) => boolean) => setPausedState((p) => {
    const next = fn(p);
    if (next) PAUSED_TERMINALS.add(imei); else PAUSED_TERMINALS.delete(imei);
    return next;
  });
  const logEl = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(null);
    setPausedState(PAUSED_TERMINALS.has(imei));
  }, [imei]);

  // the shared hardened tail (single-flight, hidden-guard, dedupe, bounded);
  // paused freezes the VIEW only — events are DB-backed, resume catches up
  const { items: events, clear: clearBuf } = useCursorTail<FotaEvent>({
    fetch: async (c) => (await fota.events(imei, (c as number | undefined) ?? 0)).events,
    cursorOf: (e) => e.id,
    max: 500,
    intervalMs: 1200,
    paused,
    resetKey: imei,
  });

  useEffect(() => {
    if (!paused && logEl.current) logEl.current.scrollTop = logEl.current.scrollHeight;
  }, [events, paused]);

  const clear = useMutation({
    mutationFn: () => fota.clearEvents(imei),
    onSuccess: clearBuf,
  });
  // stable identity so memoized EventLines don't re-render on every append
  const toggleOpen = useCallback((id: number) => setOpen((cur) => (cur === id ? null : id)), []);

  return (
    <div className="panel panel-pad" style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <span className="mono" style={{ color: "var(--t-bright)", fontSize: 12.5, fontWeight: 600 }}>{imei}</span>
        {paused && <span style={{ color: "var(--warn)", fontWeight: 700, fontSize: 11 }}>❚❚ PAUSED</span>}
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--t-mute)" }}>{events.length}</span>
        <button className={`btn ${paused ? "teal" : "ghost"}`} onClick={() => setPaused((p) => !p)}
          title={paused ? "resume (catches up)" : "pause so you can read"}>
          <Icon name={paused ? "play" : "pause"} />
        </button>
        <button className="btn ghost" onClick={() => clear.mutate()} title="clear this terminal">Clear</button>
        <button className="btn red" onClick={onDisarm} title="disarm + close this terminal"
         ><Icon name="close" /></button>
      </div>

      <div
        ref={logEl}
        className="mono"
        style={{
          height: 340,
          overflowY: "auto",
          background: "var(--panel2, rgba(0,0,0,.16))",
          border: "1px solid var(--line2)",
          borderRadius: 8,
          padding: "8px 10px",
          fontSize: 12,
          lineHeight: 1.5,
        }}
      >
        {events.length === 0 && (
          <div className="muted">Waiting for events… run a FOTA update against this IMEI.</div>
        )}
        {events.map((e) => (
          <EventLine key={e.id} e={e} imei={imei} expanded={open === e.id} onToggle={toggleOpen} />
        ))}
      </div>
    </div>
  );
}
